import { type ComponentProps, type ComponentPropsWithoutRef, memo, type ReactNode } from 'react';

import { VisuallyHidden } from '@air/primitive-visually-hidden';
import { cn, tailwindVariants, type VariantProps } from '@air/utils-tailwind';

import { NotificationsItemLayout } from './NotificationsItemLayout';

export const notification = tailwindVariants({
  base: 'group/notifications-item relative flex w-full min-w-0 max-w-full items-start px-4',
  variants: {
    state: {
      read: 'ads:bg-base bg-gray-1 hover:ads:bg-subtle hover:bg-gray-3',
      unread: 'ads:bg-base bg-gray-2 ui2026:bg-gray-1 hover:ads:bg-subtle hover:bg-gray-3',
    },
  },
});

export type NotificationVariants = VariantProps<typeof notification>;

export type NotificationsItemProps = Omit<ComponentPropsWithoutRef<'div'>, 'prefix' | 'title'> &
  Pick<ComponentProps<'button'>, 'onClick'> &
  NotificationVariants & {
    content?: ReactNode;
    createdAt: string;
    isUI2: boolean;
    meta?: ReactNode;
    prefix?: ReactNode;
    suffix?: ReactNode;
    title?: ReactNode;
  };

export const NotificationsItem = memo(
  ({
    className,
    content,
    createdAt,
    isUI2,
    onClick,
    prefix,
    suffix,
    meta,
    state = 'unread',
    title,
    ...restOfProps
  }: NotificationsItemProps) => {
    return (
      <NotificationsItemLayout
        className={notification({ className, state })}
        content={content}
        createdAt={createdAt}
        data-state={state}
        data-testid="NOTIFICATIONS_ITEM"
        isUI2={isUI2}
        meta={meta}
        prefix={prefix}
        prefixAddon={
          state === 'unread' && (
            <div
              className={cn(
                'absolute h-2 w-2 rounded-full ads:group-hover/notifications-item:ring-subtle group-hover/notifications-item:ring-gray-3',
                isUI2
                  ? '-top-0.5 -right-0.5 ads:bg-error bg-red-9 ring-2 ring-gray-1'
                  : 'top-0 left-0 ads:bg-error bg-red-8 ring-1 ring-gray-2',
              )}
              data-testid="NOTIFICATIONS_ITEM_DOT"
            />
          )
        }
        suffix={suffix}
        title={title}
        {...restOfProps}
      >
        <button
          className={cn(
            'absolute inset-0 h-full w-full border-0 bg-transparent',
            onClick ? 'cursor-pointer' : 'cursor-default',
          )}
          data-testid="NOTIFICATIONS_ITEM_BUTTON"
          onClick={onClick}
          title={typeof meta === 'string' ? meta : undefined}
          type="button"
        >
          <VisuallyHidden>Select notification</VisuallyHidden>
        </button>
      </NotificationsItemLayout>
    );
  },
);

NotificationsItem.displayName = 'NotificationsItem';
